import { Center, Group, Stack, Text, useMantineTheme } from '@mantine/core';
import {
  IconArrowNarrowRightDashed,
  IconBriefcase,
  IconFileDollar,
  IconForms,
  IconMapDollar,
  IconNumber123,
  IconPlayerPause,
  IconPlayerPlay,
  IconUserDollar,
  type IconAlarm as TablerIcon,
} from '@tabler/icons-react';
import { i18nKeys, useTranslation } from 'locales';
import { motion } from 'motion/react';

import type { Automation } from '../../../../api';
import { useAutomationWizardController } from '../../controller';

import type { ActionCardProps } from './ActionCard';

type Icon = React.ComponentType<React.ComponentProps<typeof TablerIcon>>;
type IconConfig = { icon: Icon; style?: React.CSSProperties };

export const ChooseActionType = ({ actionIndex }: ActionCardProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const {
    automation,
    actions: { setActionType, getAvailableActionTypes },
  } = useAutomationWizardController();

  const actionTypeIcons = getActionTypeIcons(automation.targetModel);

  return (
    <motion.div variants={STAGGER_VARIANTS} initial="start" animate="end" style={{ width: '100%' }}>
      <Group gap="xl" mt="md" align="start" justify="center" w="100%">
        {getAvailableActionTypes().map(field => {
          const iconConfig = actionTypeIcons[field.type];
          const icons = Array.isArray(iconConfig) ? iconConfig : [iconConfig];

          return (
            <motion.div
              key={field.type}
              variants={ITEM_VARIANTS}
              whileHover={{ scale: 1.05, transition: { duration: 0.05 } }}
              style={{
                pointerEvents: field.enabled ? 'auto' : 'none',
              }}
            >
              <Stack
                gap={6}
                style={{
                  cursor: field.enabled ? 'pointer' : 'default',
                  opacity: field.enabled ? 1 : 0.4,
                  pointerEvents: field.enabled ? 'auto' : 'none',
                }}
                onClick={() => {
                  if (!field.enabled) return;
                  setActionType(actionIndex, field.type);
                }}
              >
                <Center
                  py="lg"
                  bg={theme.colors.gray[0]}
                  bdrs={16}
                  style={{ border: `1px solid ${theme.colors.gray[3]}` }}
                >
                  {icons.map((iconItem, index) => {
                    const IconComponent = iconItem.icon;
                    return (
                      <IconComponent key={index} size={30} stroke={1.5} style={iconItem.style} />
                    );
                  })}
                </Center>
                <Text size="sm" ta="center" w="120">
                  {t((i18nKeys.AUTOMATION.ACTION_TYPES as any)[field.type.toUpperCase()])}
                </Text>
              </Stack>
            </motion.div>
          );
        })}
      </Group>
    </motion.div>
  );
};

export const getActionTypeIcons = (model: Automation['targetModel']) => {
  const ARROW: IconConfig = { icon: IconArrowNarrowRightDashed, style: { width: '24px' } };
  const modelIcon = model === 'debtor' ? IconUserDollar : IconFileDollar;

  const iconBuilder = (from: Icon, to: Icon) => [{ icon: from }, ARROW, { icon: to }];

  return {
    CopyValueToField: iconBuilder(IconNumber123, IconForms),
    CopyFieldToField: iconBuilder(IconForms, IconForms),
    AssignAccountManager: iconBuilder(modelIcon, IconBriefcase),
    AssignRecoveryPlan: iconBuilder(modelIcon, IconMapDollar),
    Pause: iconBuilder(modelIcon, IconPlayerPause),
    Unpause: iconBuilder(modelIcon, IconPlayerPlay),
  };
};

const STAGGER_VARIANTS = {
  start: {},
  end: { transition: { staggerChildren: 0.04 } },
};

const ITEM_VARIANTS = {
  start: { opacity: 0, scale: 0.1 },
  end: { opacity: 1, scale: 1, transition: { duration: 0.25 } },
};
